import { memo, ReactNode, useEffect, useState } from 'react';
import { Box, FelaCSS } from '@bridebook/ui';
import componentStyles from './carousel-grid-gallery.style';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { getAllSupplierPhotos } from '@bridebook/web/app-basic/lib/supplier/selectors';
import { getIsMobile, getIsTablet } from '@bridebook/web/app-basic/lib/app/selectors';
import LightweightCarousel from '@bridebook/web/app-shared/components/supplier/supplier-card/lightweight-carousel';
import { Thumbnail } from '@bridebook/web/components/blocks/search-list-item';
import GalleryModal from './gallery-modal';
import { useTranslation } from 'next-i18next';
import { Icon } from '@bridebook/aura';
import { Image } from '@bridebook/aura/src/icons';
import mergeStyles from '@bridebook/ui/src/fela-utils/merge-styles';
import { SaveToShortlistFakeButton } from '@bridebook/web/app-basic/components/save-to-shortlist-fake-button';

interface IProps {
  styleOverrides?: {
    carousel?: FelaCSS;
    gallery?: FelaCSS;
    galleryItem?: FelaCSS;
    thumbProps?: {
      width: number;
      height: number;
    };
  };
  controlsOverride?: ReactNode;
}

const CarouselGridGalleryBase = ({ styleOverrides, controlsOverride }: IProps) => {
  const { t } = useTranslation('common');
  const styles = componentStyles();
  const [showGallery, setShowGallery] = useState(false);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const photos = useSelector(getAllSupplierPhotos).map((item) => item.path);
  const isMobile = useSelector(getIsMobile);
  const isTablet = useSelector(getIsTablet);

  useEffect(() => {
    if (showGallery) document.body.style.overflowY = 'hidden';
    else document.body.style.overflowY = 'auto';
  }, [showGallery]);

  return (
    <Box style={styles.wrapper}>
      <Box style={mergeStyles([styles.carousel, styleOverrides?.carousel || {}])}>
        <LightweightCarousel
          controls
          eagerLoad
          isSSR
          isVenue
          suppliedImages={photos}
          showCarousel={mounted}
          isMobile={isMobile}
          isTablet={isTablet}
        />
        <div className="absolute top-4 right-4 z-10">
          <SaveToShortlistFakeButton />
        </div>
        {(isMobile || isTablet) && (
          <Box style={styles.viewAll} as="button">
            <Icon icon={Image} size={16} />
            <Box as="span">({photos.length})</Box>
          </Box>
        )}
      </Box>

      {!isMobile && !isTablet && (
        <Box style={mergeStyles([styles.gallery, styleOverrides?.gallery || {}])}>
          {photos.slice(photos.length < 5 ? 0 : 1, 5).map((src, index) => (
            <Thumbnail
              isSSR
              width={235}
              height={193}
              key={index}
              src={src}
              style={mergeStyles([styles.galleryItem, styleOverrides?.galleryItem || {}])}
              {...styleOverrides?.thumbProps}
            />
          ))}
          {controlsOverride ?? (
            <Box style={styles.viewAll} as="button">
              <Icon icon={Image} size={16} />
              <Box as="span">
                {t('viewAll')} ({photos.length})
              </Box>
            </Box>
          )}
        </Box>
      )}
      {showGallery && <GalleryModal onBack={() => setShowGallery(false)} />}
    </Box>
  );
};

export default memo(CarouselGridGalleryBase);
